import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionDivider from './SectionDivider';

export default function SplitSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'image-left';

    const sectionButtons = content.buttons && content.buttons.length > 0
        ? content.buttons
        : content.cta_text ? [{ text: content.cta_text, link: content.cta_link || '#contact', style: 'solid', icon: '', bg_color: '', text_color: '' }] : [];

    const features = (content.features || '').split('\n').filter(Boolean);
    const imageRight = variant === 'image-right';

    const folderImages = content.image_folder_id && mediaFolders ? (mediaFolders[content.image_folder_id] || []) : [];
    const image = folderImages[0]?.image_url || content.image_url || null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const imageBlock = (
        <div className={`md:w-1/2 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : (imageRight ? 'opacity-0 translate-x-8' : 'opacity-0 -translate-x-8')}`}>
            {image ? (
                <div className="rounded-2xl overflow-hidden shadow-xl">
                    <img src={image} alt={content.title || ''} className="w-full object-cover" loading="lazy" />
                </div>
            ) : (
                <div className="aspect-[4/3] rounded-2xl flex items-center justify-center" style={{ backgroundColor: hexToRgba(s.accent, 0.03) }}>
                    <svg className="w-20 h-20 opacity-15" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                </div>
            )}
        </div>
    );

    const contentBlock = (
        <div className={`md:w-1/2 flex flex-col justify-center transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : (imageRight ? 'opacity-0 -translate-x-8' : 'opacity-0 translate-x-8')}`}>
            {content.tag && <span className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: s.accent }}>{content.tag}</span>}
            {content.title && <h2 className="text-2xl md:text-3xl font-bold leading-tight" style={{ color: s.text }}>{content.title}</h2>}
            {content.body && <p className="text-base mt-4 leading-relaxed" style={{ color: s.textSub }}>{content.body}</p>}
            {features.length > 0 && (
                <div className="mt-5 space-y-2.5">
                    {features.map((f, i) => (
                        <div key={i} className="flex items-center gap-2.5 text-sm">
                            <svg className="w-5 h-5 shrink-0" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                            <span style={{ color: s.text }}>{f}</span>
                        </div>
                    ))}
                </div>
            )}
            {sectionButtons.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-3">
                    {sectionButtons.map((btn, i) => {
                        const btnStyle = btn.style || 'solid';
                        const icon = btn.icon && getButtonIcon(resolveIconKey(btn.icon));
                        const lp = resolveLinkProps(btn.link || '#');
                        const customBg = btn.bg_color;
                        const customText = btn.text_color;
                        let cls = 'px-6 py-3 rounded-lg font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ';
                        let style = {};
                        if (btnStyle === 'solid' || btnStyle === 'pill') {
                            if (customBg) { style.backgroundColor = customBg; style.color = customText || (isColorDark(customBg) ? '#fff' : '#111'); cls += 'shadow-lg hover:shadow-xl'; }
                            else { cls += 'text-white shadow-lg hover:shadow-xl'; style.backgroundColor = s.accent; }
                        } else if (btnStyle === 'outline') {
                            if (customBg) { style.color = customBg; style.borderColor = customBg; cls += 'border-2'; }
                            else { style.color = s.accent; style.borderColor = s.accent; cls += 'border-2 hover:opacity-80'; }
                        } else { style.color = customText || s.accent; cls += 'hover:opacity-80'; }
                        if (btnStyle === 'pill') cls = cls.replace('rounded-lg', 'rounded-full');
                        if (customText && (btnStyle === 'solid' || btnStyle === 'pill')) style.color = customText;
                        return <a key={i} {...lp} className={cls} style={style}>{icon}{btn.text}</a>;
                    })}
                </div>
            )}
        </div>
    );

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div className={`flex flex-col ${imageRight ? 'md:flex-row-reverse' : 'md:flex-row'} gap-10 md:gap-16 items-center`}>
                    {imageBlock}
                    {contentBlock}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
